import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Accesso non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b-2 border-white/25 py-3 text-lg font-medium text-white placeholder:text-white/35 focus:outline-none focus:border-[#d9381e] transition-colors";

  return (
    <div className="min-h-screen bg-[#0d2c54] text-white flex flex-col">
      <div className="grain" aria-hidden="true" />
      <div className="p-5 md:p-10">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 font-mono-num uppercase text-xs tracking-widest text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Torna al sito
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-5">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <span className="font-mono-num uppercase text-xs tracking-[0.25em] text-[#d9381e]">
            Area riservata
          </span>
          <h1 className="mt-3 font-display font-black uppercase tracking-tighter text-5xl md:text-6xl leading-[0.85]">
            Pannello
            <br />
            di gestione
          </h1>

          <form onSubmit={submit} data-testid="login-form" className="mt-10 space-y-8">
            <div>
              <label className="font-mono-num uppercase text-[11px] tracking-widest text-white/50">
                Email
              </label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@…"
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-mono-num uppercase text-[11px] tracking-widest text-white/50">
                Password
              </label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={inputCls}
              />
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#d9381e] text-white py-4 font-display font-bold uppercase tracking-wide text-lg hover:bg-white hover:text-[#0d2c54] transition-colors disabled:opacity-60"
            >
              {loading ? "Accesso…" : "Entra →"}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
